import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { addFinalSummary, updateFinalSummary } from "../../Redux/Actions/bookingActions";
import toast from "react-hot-toast";
import "./finalSummaryPopup.css";

const FinalSummaryPopup = ({ bookingId, existingData, currency, onClose, onSuccess }) => {
    const dispatch = useDispatch();
    const isEdit = existingData && Object.keys(existingData).length > 0;
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        total_amount: "",
        gst_agent: "",
        gst_admin: "",
        taxes: "",
        taxes_type: "flat",
        tds: "",
        tds_type: "flat",
        agent_margin: "",
        admin_margin: "",
        notes: "",
    });

    const [dynamicFields, setDynamicFields] = useState([]);
    const [newFieldKey, setNewFieldKey] = useState("");
    const [newFieldValue, setNewFieldValue] = useState("");
    const [newFieldType, setNewFieldType] = useState("flat");
    const [newFieldError, setNewFieldError] = useState("");

    // ── Load existing data ──
    useEffect(() => {
        if (isEdit && existingData) {
            const deduction = existingData.deduction || {};
            setFormData({
                total_amount: existingData.total_amount ?? "",
                gst_agent: existingData.gst_agent ?? "",
                gst_admin: existingData.gst_admin ?? "",
                taxes: deduction._taxes?.value ?? "",
                taxes_type: deduction._taxes?.type ?? "flat",
                tds: deduction._tds?.value ?? "",
                tds_type: deduction._tds?.type ?? "flat",
                agent_margin: existingData.agent_margin ?? "",
                admin_margin: existingData.admin_margin ?? "",
                notes: existingData.notes ?? "",
            });

            const dynamic = Object.entries(deduction)
                .filter(([key]) => !["_taxes", "_tds"].includes(key) && key.startsWith("_"))
                .map(([key, val]) => ({
                    key,
                    value: val?.value ?? val,
                    type: val?.type ?? "flat",
                }));

            setDynamicFields(dynamic);
        }
    }, [existingData, isEdit]);

    // ── Derived calculations ──
    const totalAmount = Number(formData.total_amount) || 0;
    const gstPct = Number(formData.gst) || 0;
    const gstAmount = (totalAmount * gstPct) / 100;

    const resolvePctOrFlat = (value, type) => {
        const num = Number(value) || 0;
        return type === "pct" ? (totalAmount * num) / 100 : num;
    };

    const taxesAmount = resolvePctOrFlat(formData.taxes, formData.taxes_type);
    const tdsAmount = resolvePctOrFlat(formData.tds, formData.tds_type);
    const dynamicTotal = dynamicFields.reduce((sum, f) => {
        return sum + resolvePctOrFlat(f.value, f.type);
    }, 0);

    const remainingAmount = totalAmount;

    const agentPct = Number(formData.agent_margin) || 0;
    const adminPct = Number(formData.admin_margin) || 0;

    const agentMarginAmount = (remainingAmount * agentPct) / 100;
    const adminMarginAmount = (remainingAmount * adminPct) / 100;

    const gstAgentPct = Number(formData.gst_agent) || 0;
    const gstAdminPct = Number(formData.gst_admin) || 0;
    const gstAgentAmount = (agentMarginAmount * gstAgentPct) / 100;
    const gstAdminAmount = (totalAmount * gstAdminPct) / 100;

    const agentFinal = agentMarginAmount - taxesAmount - tdsAmount - dynamicTotal + gstAgentAmount;
    const adminFinal = adminMarginAmount + gstAdminAmount;

    // ── Handlers ──
    const handleFixedChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleAgentMarginChange = (e) => {
        const val = e.target.value;
        const num = Math.min(100, Math.max(0, Number(val)));
        setFormData((prev) => ({
            ...prev,
            agent_margin: val === "" ? "" : num,
            admin_margin: val === "" ? "" : 100 - num,
        }));
    };

    const handleAdminMarginChange = (e) => {
        const val = e.target.value;
        const num = Math.min(100, Math.max(0, Number(val)));
        setFormData((prev) => ({
            ...prev,
            admin_margin: val === "" ? "" : num,
            agent_margin: val === "" ? "" : 100 - num,
        }));
    };

    const handleDynamicChange = (index, field, value) => {
        setDynamicFields((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const handleAddDynamicField = () => {
        if (!newFieldKey.trim()) {
            setNewFieldError("Key is required");
            return;
        }
        const keyFormatted = "_" + newFieldKey.trim().toLowerCase().replace(/\s+/g, "_");
        if (dynamicFields.find((f) => f.key === keyFormatted)) {
            setNewFieldError("Key already exists");
            return;
        }
        setDynamicFields((prev) => [...prev, { key: keyFormatted, value: newFieldValue, type: newFieldType }]);
        setNewFieldKey("");
        setNewFieldValue("");
        setNewFieldType("flat");
        setNewFieldError("");
    };

    const handleRemoveDynamicField = (index) => {
        setDynamicFields((prev) => prev.filter((_, i) => i !== index));
    };

    const buildPayload = () => {
        const deduction = {
            _taxes: { value: Number(formData.taxes) || 0, type: formData.taxes_type },
            _tds: { value: Number(formData.tds) || 0, type: formData.tds_type },
        };
        dynamicFields.forEach(({ key, value, type }) => {
            if (key) deduction[key] = { value: isNaN(Number(value)) ? value : Number(value), type };
        });

        return {
            id: bookingId,
            final_summary: {
                total_amount: totalAmount,
                gst_agent: gstAgentPct,
                gst_admin: gstAdminPct,
                deduction,
                agent_margin: agentPct,
                admin_margin: adminPct,
                notes: formData.notes || "",
            },
        };
    };

    const handleSubmit = async () => {
        if (!formData.total_amount) {
            toast.error("Total amount is required");
            return;
        }
        try {
            setLoading(true);
            const payload = buildPayload();
            const action = isEdit ? updateFinalSummary : addFinalSummary;
            const res = await dispatch(action(payload));
            if (res?.statusCode === 200) {
                toast.success(isEdit ? "Final summary updated!" : "Final summary added!");
                onSuccess();
            } else {
                toast.error(res?.message || "Something went wrong");
            }
        } catch {
            toast.error("Failed to save final summary");
        } finally {
            setLoading(false);
        }
    };

    const TypeToggle = ({ value, onChange, disabled }) => (
        <div className="fsp-type-toggle">
            <button
                type="button"
                className={`fsp-type-btn ${value === "flat" ? "fsp-type-btn--active" : ""}`}
                onClick={() => onChange("flat")}
                disabled={disabled}
            >
                {currency || "₹"}
            </button>
            <button
                type="button"
                className={`fsp-type-btn ${value === "pct" ? "fsp-type-btn--active" : ""}`}
                onClick={() => onChange("pct")}
                disabled={disabled}
            >
                %
            </button>
        </div>
    );

    return (
        <div className="fsp-overlay">
            <div className="fsp-modal">

                {/* ── Head ── */}
                <div className="fsp-modal__head">
                    <div className="fsp-modal__head-left">
                        <div className="fsp-modal__icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 11l3 3L22 4" />
                                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                            </svg>
                        </div>
                        <div>
                            <p className="fsp-modal__title">{isEdit ? "Update Final Summary" : "Add Final Summary"}</p>
                            <p className="fsp-modal__subtitle">Fill in the booking financial summary</p>
                        </div>
                    </div>
                    <button className="fsp-modal__close" onClick={onClose} disabled={loading}>✕</button>
                </div>

                <div className="fsp-modal__body">

                    {/* ── Total Amount ── */}
                    <div className="fsp-section">
                        <div className="fsp-section__title">Total Amount</div>
                        <div className="fsp-field">
                            <label className="fsp-label">Total Amount <span className="fsp-req">*</span></label>
                            <div className="fsp-input-wrap">
                                <span className="fsp-input-prefix">{currency || "₹"}</span>
                                <input
                                    className="fsp-input fsp-input--prefixed"
                                    type="number"
                                    name="total_amount"
                                    value={formData.total_amount}
                                    onChange={handleFixedChange}
                                    onWheel={(e) => e.target.blur()}
                                    placeholder="0"
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="fsp-divider" />

                    {/* ── GST ── */}
                    <div className="fsp-section">
                        <div className="fsp-section__title">GST</div>
                        <div className="fsp-grid-2">
                            <div className="fsp-field">
                                <label className="fsp-label fsp-label--agent">Agent GST %</label>
                                <div className="fsp-input-wrap">
                                    <span className="fsp-input-prefix">%</span>
                                    <input
                                        className="fsp-input fsp-input--prefixed fsp-input--agent"
                                        type="number"
                                        name="gst_agent"
                                        value={formData.gst_agent}
                                        onChange={handleFixedChange}
                                        onWheel={(e) => e.target.blur()}
                                        placeholder="0"
                                        min={0}
                                        max={100}
                                        disabled={loading}
                                    />
                                </div>
                                {gstAgentPct > 0 && agentMarginAmount > 0 && (
                                    <div className="fsp-calc-chip fsp-calc-chip--gst">
                                        = {currency || "₹"} {gstAgentAmount.toLocaleString("en-IN", { maximumFractionDigits: 2 })} → added to Agent
                                    </div>
                                )}
                            </div>

                            <div className="fsp-field">
                                <label className="fsp-label fsp-label--admin">Admin GST %</label>
                                <div className="fsp-input-wrap">
                                    <span className="fsp-input-prefix">%</span>
                                    <input
                                        className="fsp-input fsp-input--prefixed fsp-input--admin"
                                        type="number"
                                        name="gst_admin"
                                        value={formData.gst_admin}
                                        onChange={handleFixedChange}
                                        onWheel={(e) => e.target.blur()}
                                        placeholder="0"
                                        min={0}
                                        max={100}
                                        disabled={loading}
                                    />
                                </div>
                                {gstAdminPct > 0 && totalAmount > 0 && (
                                    <div className="fsp-calc-chip fsp-calc-chip--gst">
                                        = {currency || "₹"} {gstAdminAmount.toLocaleString("en-IN", { maximumFractionDigits: 2 })} → added to Admin
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="fsp-divider" />

                    {/* ── Tax & Deductions ── */}
                    <div className="fsp-section">
                        <div className="fsp-section__title">Tax & Deductions</div>
                        <p className="fsp-section__hint">Deducted from Agent's share</p>
                        <div className="fsp-grid-2">

                            {/* Taxes */}
                            <div className="fsp-field">
                                <label className="fsp-label">Taxes</label>
                                <div className="fsp-input-row">
                                    <TypeToggle
                                        value={formData.taxes_type}
                                        onChange={(v) => setFormData((p) => ({ ...p, taxes_type: v }))}
                                        disabled={loading}
                                    />
                                    <div className="fsp-input-wrap fsp-input-wrap--grow">
                                        <span className="fsp-input-prefix">
                                            {formData.taxes_type === "pct" ? "%" : (currency || "₹")}
                                        </span>
                                        <input
                                            className="fsp-input fsp-input--prefixed"
                                            type="number"
                                            name="taxes"
                                            value={formData.taxes}
                                            onChange={handleFixedChange}
                                            onWheel={(e) => e.target.blur()}
                                            placeholder="0"
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                                {formData.taxes_type === "pct" && Number(formData.taxes) > 0 && totalAmount > 0 && (
                                    <div className="fsp-calc-chip">
                                        = {currency || "₹"} {taxesAmount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                                    </div>
                                )}
                            </div>

                            {/* TDS */}
                            <div className="fsp-field">
                                <label className="fsp-label">TDS</label>
                                <div className="fsp-input-row">
                                    <TypeToggle
                                        value={formData.tds_type}
                                        onChange={(v) => setFormData((p) => ({ ...p, tds_type: v }))}
                                        disabled={loading}
                                    />
                                    <div className="fsp-input-wrap fsp-input-wrap--grow">
                                        <span className="fsp-input-prefix">
                                            {formData.tds_type === "pct" ? "%" : (currency || "₹")}
                                        </span>
                                        <input
                                            className="fsp-input fsp-input--prefixed"
                                            type="number"
                                            name="tds"
                                            value={formData.tds}
                                            onChange={handleFixedChange}
                                            onWheel={(e) => e.target.blur()}
                                            placeholder="0"
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                                {formData.tds_type === "pct" && Number(formData.tds) > 0 && totalAmount > 0 && (
                                    <div className="fsp-calc-chip">
                                        = {currency || "₹"} {tdsAmount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                                    </div>
                                )}
                            </div>

                            {/* Dynamic fields */}
                            {dynamicFields.map((field, index) => (
                                <div className="fsp-field fsp-field--dynamic" key={index}>
                                    <input
                                        className="fsp-input fsp-input--key"
                                        type="text"
                                        value={field.key.replace(/^_/, "")}
                                        onChange={(e) => handleDynamicChange(index, "key", "_" + e.target.value.toLowerCase().replace(/\s+/g, "_"))}
                                        placeholder="Field name"
                                        disabled={loading}
                                    />
                                    <div className="fsp-input-row">
                                        <TypeToggle
                                            value={field.type}
                                            onChange={(v) => handleDynamicChange(index, "type", v)}
                                            disabled={loading}
                                        />
                                        <div className="fsp-input-wrap fsp-input-wrap--grow">
                                            <span className="fsp-input-prefix">
                                                {field.type === "pct" ? "%" : (currency || "₹")}
                                            </span>
                                            <input
                                                className="fsp-input fsp-input--prefixed"
                                                type="number"
                                                value={field.value}
                                                onChange={(e) => handleDynamicChange(index, "value", e.target.value)}
                                                onWheel={(e) => e.target.blur()}
                                                placeholder="0"
                                                disabled={loading}
                                            />
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="fsp-remove-btn"
                                        onClick={() => handleRemoveDynamicField(index)}
                                        disabled={loading}
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}
                        </div>

                        {/* Add new field */}
                        <div className="fsp-add-field">
                            <input
                                className={`fsp-input ${newFieldError ? "fsp-input--error" : ""}`}
                                type="text"
                                value={newFieldKey}
                                onChange={(e) => { setNewFieldKey(e.target.value); setNewFieldError(""); }}
                                placeholder="New field key (e.g. stamp_duty)"
                                disabled={loading}
                            />
                            <TypeToggle
                                value={newFieldType}
                                onChange={setNewFieldType}
                                disabled={loading}
                            />
                            <input
                                className="fsp-input"
                                type="number"
                                value={newFieldValue}
                                onChange={(e) => setNewFieldValue(e.target.value)}
                                onWheel={(e) => e.target.blur()}
                                placeholder="Value"
                                disabled={loading}
                            />
                            <button
                                type="button"
                                className="fsp-add-btn"
                                onClick={handleAddDynamicField}
                                disabled={loading}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <line x1="12" y1="5" x2="12" y2="19" />
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                </svg>
                                Add Field
                            </button>
                        </div>
                        {newFieldError && <p className="fsp-error-msg">{newFieldError}</p>}
                    </div>

                    <div className="fsp-divider" />

                    {/* ── Margin Details ── */}
                    <div className="fsp-section">
                        <div className="fsp-section__title">Margin Details</div>

                        {/* Remaining Amount Box */}
                        <div className="fsp-remaining-box">
                            <div>
                                <p className="fsp-remaining-label">Base for margin split</p>
                                <p className="fsp-remaining-sub">Agent & Admin % applied on Total Amount</p>
                            </div>
                            <span className="fsp-remaining-value">
                                {currency || "₹"} {Math.max(0, remainingAmount).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                            </span>
                        </div>

                        {/* Split bar */}
                        {(agentPct > 0 || adminPct > 0) && (
                            <div className="fsp-margin-bar-wrap">
                                <div className="fsp-margin-bar">
                                    <div className="fsp-margin-bar__agent" style={{ width: `${agentPct}%` }} />
                                    <div className="fsp-margin-bar__admin" style={{ width: `${adminPct}%` }} />
                                </div>
                                <div className="fsp-margin-bar__labels">
                                    <span className="fsp-margin-bar__label--agent">Agent {agentPct}%</span>
                                    <span className="fsp-margin-bar__label--admin">Admin {adminPct}%</span>
                                </div>
                            </div>
                        )}

                        <div className="fsp-grid-2" style={{ marginTop: "14px" }}>

                            {/* Agent Margin */}
                            <div className="fsp-field">
                                <label className="fsp-label fsp-label--agent">Agent Margin %</label>
                                <div className="fsp-input-wrap">
                                    <span className="fsp-input-prefix">%</span>
                                    <input
                                        className="fsp-input fsp-input--prefixed fsp-input--agent"
                                        type="number"
                                        name="agent_margin"
                                        value={formData.agent_margin}
                                        onChange={handleAgentMarginChange}
                                        onWheel={(e) => e.target.blur()}
                                        placeholder="0"
                                        min={0}
                                        max={100}
                                        disabled={loading}
                                    />
                                </div>
                                {agentPct > 0 && remainingAmount > 0 && (
                                    <div className="fsp-margin-calc fsp-margin-calc--agent">
                                        <div className="fsp-margin-calc__row">
                                            <span>Margin ({agentPct}%)</span>
                                            <span>{currency || "₹"} {agentMarginAmount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                                        </div>
                                        <div className="fsp-margin-calc__row fsp-margin-calc__row--add">
                                            <span>− Taxes & TDS & Other</span>
                                            <span>− {currency || "₹"} {(taxesAmount + tdsAmount + dynamicTotal).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                                        </div>
                                        <div className="fsp-margin-calc__row fsp-margin-calc__row--add">
                                            <span>+ GST ({gstAgentPct}%)</span>
                                            <span>+ {currency || "₹"} {gstAgentAmount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                                        </div>
                                        <div className="fsp-margin-calc__row fsp-margin-calc__row--total">
                                            <span>Agent Final</span>
                                            <span>{currency || "₹"} {agentFinal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Admin Margin */}
                            <div className="fsp-field">
                                <label className="fsp-label fsp-label--admin">Admin Margin %</label>
                                <div className="fsp-input-wrap">
                                    <span className="fsp-input-prefix">%</span>
                                    <input
                                        className="fsp-input fsp-input--prefixed fsp-input--admin"
                                        type="number"
                                        name="admin_margin"
                                        value={formData.admin_margin}
                                        onChange={handleAdminMarginChange}
                                        onWheel={(e) => e.target.blur()}
                                        placeholder="0"
                                        min={0}
                                        max={100}
                                        disabled={loading}
                                    />
                                </div>
                                {adminPct > 0 && remainingAmount > 0 && (
                                    <div className="fsp-margin-calc fsp-margin-calc--admin">
                                        <div className="fsp-margin-calc__row">
                                            <span>Margin ({adminPct}%)</span>
                                            <span>{currency || "₹"} {adminMarginAmount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                                        </div>
                                        <div className="fsp-margin-calc__row fsp-margin-calc__row--add">
                                            <span>+ GST ({gstAdminPct}%)</span>
                                            <span>+ {currency || "₹"} {gstAdminAmount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                                        </div>
                                        <div className="fsp-margin-calc__row fsp-margin-calc__row--total">
                                            <span>Admin Final</span>
                                            <span>{currency || "₹"} {adminFinal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="fsp-divider" />

<div className="fsp-section">
    <div className="fsp-section__title">Notes</div>
    <textarea
        className="fsp-textarea"
        name="notes"
        value={formData.notes}
        onChange={handleFixedChange}
        placeholder="Add any remarks or notes for this summary..."
        rows={3}
        disabled={loading}
    />
</div>

                </div>

                <div className="fsp-modal__foot">
                    <button className="fsp-btn fsp-btn--secondary" onClick={onClose} disabled={loading}>
                        Cancel
                    </button>
                    <button className="fsp-btn fsp-btn--primary" onClick={handleSubmit} disabled={loading}>
                        {loading ? "Saving..." : isEdit ? "Update Summary" : "Save Summary"}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default FinalSummaryPopup;